/**
 * The outlines under an area report's map — land, borders, coast, lakes,
 * rivers and provincial lines — as `scripts/build-area-outlines.mjs` cut them
 * out of Natural Earth. Task 10.37; decided 2026-09-28 in `project-scope.md`.
 * **And the names of the rivers and lakes**, since 2026-09-29 (`waterLabels`),
 * **and the larger towns** (`placeTowns`), the same day.
 *
 * Pure: decoding a file and turning it into SVG paths and labels for one map.
 * The fetch is `AreaMap.tsx`'s.
 */

type Box = readonly [number, number, number, number];

/** A named river's pieces, or a lake, as the script writes them. `n` is the
 *  name (Swedish where Natural Earth has one), `r` Natural Earth's rank —
 *  lower for the more important. */
type EncodedRiver = {
  readonly n: string | null;
  readonly r: number;
  readonly l: number[][];
};
type EncodedLake = {
  readonly n: string | null;
  readonly r: number;
  readonly g: number[];
};

/**
 * One cluster of a country's file, still encoded. **Rivers and lakes may be
 * bare arrays**, the shape before they carried names: a file the browser or a
 * cache kept from before 2026-09-29 is drawn without names rather than not at
 * all.
 */
type EncodedCluster = {
  readonly bbox: Box;
  readonly land: readonly {
    readonly code: string;
    readonly rings: readonly number[][];
  }[];
  readonly lakes: readonly (EncodedLake | number[])[];
  readonly rivers: readonly (EncodedRiver | number[])[];
  readonly admin: readonly number[][];
  /** Absent in a file from before the towns were added: no towns, then. */
  readonly towns?: readonly EncodedTown[];
};

/** A town as the script writes it: name, population, and the point in
 *  thousandths of a degree — not a step, since there is only one. */
type EncodedTown = {
  readonly n: string;
  readonly p: number;
  readonly x: number;
  readonly y: number;
};

export type OutlineFile = {
  readonly precision: number;
  readonly clusters: readonly EncodedCluster[];
};

/** SVG path data for one map, by what it draws. */
export type OutlinePaths = {
  /** The report's own country, and its neighbours, apart so they can be told
   *  apart: the country is the land the report is about. */
  readonly home: string;
  readonly neighbours: string;
  readonly lakes: string;
  readonly rivers: string;
  readonly admin: string;
  readonly labels: readonly WaterLabel[];
  readonly towns: readonly TownLabel[];
};

/** A town, placed: its dot, and its name beside it on the side that fitted. */
export type TownLabel = {
  readonly x: number;
  readonly y: number;
  readonly labelX: number;
  readonly labelY: number;
  readonly anchor: "start" | "end";
  readonly text: string;
};

/** A river's or a lake's name, placed: centre, turn in degrees, and text. */
export type WaterLabel = {
  readonly x: number;
  readonly y: number;
  readonly angle: number;
  readonly text: string;
};

/** A box on the drawing a water label must not cover — a region's point or
 *  label. */
export type Obstacle = {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
};

/**
 * Whether a fetched body is a file this can read. Parsed rather than trusted:
 * a stale file from an older script, or an error page served as JSON, should
 * leave the map without outlines rather than break it.
 */
export function isOutlineFile(value: unknown): value is OutlineFile {
  if (typeof value !== "object" || value === null) return false;
  const file = value as { precision?: unknown; clusters?: unknown };
  return (
    typeof file.precision === "number" &&
    Array.isArray(file.clusters) &&
    file.clusters.every(
      (cluster: unknown) =>
        typeof cluster === "object" &&
        cluster !== null &&
        Array.isArray((cluster as { bbox?: unknown }).bbox) &&
        Array.isArray((cluster as { land?: unknown }).land) &&
        Array.isArray((cluster as { rivers?: unknown }).rivers),
    )
  );
}

/** Integers in thousandths of a degree, each after the first a step, back to
 *  degrees. The inverse of `encode` in the script. */
export function decode(
  encoded: readonly number[],
  precision: number,
): [number, number][] {
  const points: [number, number][] = [];
  let x = 0;
  let y = 0;
  for (let i = 0; i + 1 < encoded.length; i += 2) {
    x += encoded[i] ?? 0;
    y += encoded[i + 1] ?? 0;
    points.push([x / precision, y / precision]);
  }
  return points;
}

const meets = (a: Box, b: Box) =>
  a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];

const asRiver = (entry: EncodedRiver | number[]): EncodedRiver =>
  Array.isArray(entry) ? { n: null, r: 12, l: [entry] } : entry;
const asLake = (entry: EncodedLake | number[]): EncodedLake =>
  Array.isArray(entry) ? { n: null, r: 12, g: entry } : entry;

type Point = readonly [number, number];

/**
 * The paths and water labels for one map: every cluster the map's bounds
 * reach, projected.
 *
 * A ring or line wholly outside the bounds is skipped before it is projected;
 * one that crosses the edge is drawn whole and the SVG's own viewport cuts it,
 * which is what a viewport is for.
 */
export function outlinePaths(
  file: OutlineFile,
  countryCode: string,
  bounds: Box,
  project: (lng: number, lat: number) => readonly [number, number],
  size: { readonly width: number; readonly height: number } = { width: 0, height: 0 },
  obstacles: readonly Obstacle[] = [],
): OutlinePaths {
  const shapes = {
    home: [] as string[],
    neighbours: [] as string[],
    lakes: [] as string[],
  };
  const lines = { rivers: [] as string[], admin: [] as string[] };
  const namedRivers: { name: string; rank: number; runs: Point[][] }[] = [];
  const namedLakes: { name: string; rank: number; ring: Point[] }[] = [];
  const towns: { name: string; population: number; at: Point }[] = [];

  /** Projected points, or null when nothing of it is on the map. */
  const projected = (encoded: readonly number[]): Point[] | null => {
    const points = decode(encoded, file.precision);
    if (points.length < 2) return null;
    let [w, s, e, n] = [Infinity, Infinity, -Infinity, -Infinity];
    for (const [lng, lat] of points) {
      w = Math.min(w, lng);
      e = Math.max(e, lng);
      s = Math.min(s, lat);
      n = Math.max(n, lat);
    }
    if (!meets([w, s, e, n], bounds)) return null;
    return points.map(([lng, lat]) => project(lng, lat));
  };
  const d = (points: readonly Point[], closed: boolean): string => {
    const path = points
      .map(([px, py], i) => `${i === 0 ? "M" : "L"}${px.toFixed(1)} ${py.toFixed(1)}`)
      .join("");
    return closed ? `${path}Z` : path;
  };

  for (const cluster of file.clusters) {
    if (!meets(cluster.bbox, bounds)) continue;
    for (const country of cluster.land) {
      const target = country.code === countryCode ? shapes.home : shapes.neighbours;
      for (const ring of country.rings) {
        const points = projected(ring);
        if (points !== null) target.push(d(points, true));
      }
    }
    for (const entry of cluster.lakes) {
      const lake = asLake(entry);
      const points = projected(lake.g);
      if (points === null) continue;
      shapes.lakes.push(d(points, true));
      if (lake.n !== null) namedLakes.push({ name: lake.n, rank: lake.r, ring: points });
    }
    for (const entry of cluster.rivers) {
      const river = asRiver(entry);
      const runs: Point[][] = [];
      for (const line of river.l) {
        const points = projected(line);
        if (points === null) continue;
        lines.rivers.push(d(points, false));
        runs.push(points);
      }
      if (river.n !== null && runs.length > 0) {
        namedRivers.push({ name: river.n, rank: river.r, runs });
      }
    }
    for (const line of cluster.admin) {
      const points = projected(line);
      if (points !== null) lines.admin.push(d(points, false));
    }
    for (const town of cluster.towns ?? []) {
      const lng = town.x / file.precision;
      const lat = town.y / file.precision;
      if (lng < bounds[0] || lng > bounds[2] || lat < bounds[1] || lat > bounds[3])
        continue;
      towns.push({ name: town.n, population: town.p, at: project(lng, lat) });
    }
  }

  // The towns are placed before the water's names, and the water then keeps
  // clear of them too: a town is how a reader finds their way on the map, a
  // river's name is a courtesy.
  const placedTowns = size.width > 0 ? placeTowns(towns, size, obstacles) : [];
  const townBoxes = placedTowns.flatMap(townObstacles);

  return {
    home: shapes.home.join(""),
    neighbours: shapes.neighbours.join(""),
    lakes: shapes.lakes.join(""),
    rivers: lines.rivers.join(""),
    admin: lines.admin.join(""),
    labels:
      size.width > 0
        ? waterLabels(namedRivers, namedLakes, size, [...obstacles, ...townBoxes])
        : [],
    towns: placedTowns,
  };
}

/** A town's name on the drawing, and a generous width per character. */
const TOWN_SIZE = 10;
const TOWN_CHAR_WIDTH = 5.6;
/** No more towns than this on one map, for the same reason as the water's
 *  names: past it they compete with the regions. */
const MAX_TOWNS = 8;

/**
 * Where to put the larger towns — added 2026-09-29, asked for by the owner.
 *
 * **The largest first**, a small dot with the name to its right, or its left
 * when the right is taken or off the drawing; **a town that fits neither way
 * is left out**, dot and all, because a dot without a name is a region's
 * point to a reader. Nothing may cover a region's point or label, the edge
 * or another town. Reims beside the point for Montagne de Reims is therefore
 * sometimes left out — the region already says where it is.
 */
export function placeTowns(
  towns: readonly { name: string; population: number; at: Point }[],
  size: { readonly width: number; readonly height: number },
  obstacles: readonly Obstacle[],
): TownLabel[] {
  const taken: Obstacle[] = [...obstacles];
  const placed: TownLabel[] = [];
  const sorted = [...towns].sort((a, b) => b.population - a.population);
  for (const town of sorted) {
    if (placed.length >= MAX_TOWNS) break;
    const [x, y] = town.at;
    const dot = { left: x - 4, right: x + 4, top: y - 4, bottom: y + 4 };
    for (const anchor of ["start", "end"] as const) {
      const label: TownLabel = {
        x,
        y,
        labelX: anchor === "start" ? x + 6 : x - 6,
        labelY: y + TOWN_SIZE / 3,
        anchor,
        text: town.name,
      };
      const boxes = [dot, townObstacles(label)[1] ?? dot];
      const onDrawing = boxes.every(
        (box) =>
          box.left >= 2 &&
          box.right <= size.width - 2 &&
          box.top >= 2 &&
          box.bottom <= size.height - 2,
      );
      if (!onDrawing || boxes.some((box) => taken.some((other) => overlaps(box, other))))
        continue;
      taken.push(...boxes);
      placed.push(label);
      break;
    }
  }
  return placed;
}

/** A placed town's dot and label, as boxes other labels must leave clear. */
function townObstacles(town: TownLabel): Obstacle[] {
  const width = town.text.length * TOWN_CHAR_WIDTH;
  const left = town.anchor === "start" ? town.labelX : town.labelX - width;
  return [
    { left: town.x - 4, right: town.x + 4, top: town.y - 4, bottom: town.y + 4 },
    {
      left: left - 1,
      right: left + width + 1,
      top: town.labelY - TOWN_SIZE,
      bottom: town.labelY + 3,
    },
  ];
}

/** The labels' size on the drawing, and a generous width per character. */
const LABEL_SIZE = 10;
const CHAR_WIDTH = 5.4;
/** No more names than this on one map: past it the water labels start to
 *  compete with the regions, which are what the map is for. */
const MAX_LABELS = 6;

/**
 * Where to write the rivers' and lakes' names — added 2026-09-29, asked for
 * by the owner.
 *
 * **The most important first**, by Natural Earth's rank and then by how much
 * of the water is on the map, and **only where the name fits and covers
 * nothing**: not a region's point or label, not another water name, not the
 * edge. A river is named along its longest stretch on the map, turned to
 * follow it and never upside down; a lake at its middle, when it is wide
 * enough on the map to hold its name. What does not fit goes unnamed — the
 * line is still drawn, and a name squeezed on top of a region's would cost
 * the map what it is for.
 */
export function waterLabels(
  rivers: readonly { name: string; rank: number; runs: readonly (readonly Point[])[] }[],
  lakes: readonly { name: string; rank: number; ring: readonly Point[] }[],
  size: { readonly width: number; readonly height: number },
  obstacles: readonly Obstacle[],
): WaterLabel[] {
  type Candidate = { rank: number; weight: number; label: WaterLabel; box: Obstacle };
  const candidates: Candidate[] = [];
  const inside = ([x, y]: Point) =>
    x >= 4 && x <= size.width - 4 && y >= 4 && y <= size.height - 4;

  for (const river of rivers) {
    const width = river.name.length * CHAR_WIDTH;
    // The longest stretch that stays on the drawing.
    let best: Point[] = [];
    let bestLength = 0;
    for (const run of river.runs) {
      let current: Point[] = [];
      const flush = () => {
        const length = lengthOf(current);
        if (length > bestLength) {
          best = current;
          bestLength = length;
        }
        current = [];
      };
      for (const point of run) {
        if (inside(point)) current.push(point);
        else flush();
      }
      flush();
    }
    if (bestLength < width + 16) continue;
    const from = pointAt(best, (bestLength - width) / 2);
    const to = pointAt(best, (bestLength + width) / 2);
    let angle = (Math.atan2(to[1] - from[1], to[0] - from[0]) * 180) / Math.PI;
    if (angle > 90) angle -= 180;
    if (angle < -90) angle += 180;
    // A little above the line rather than on it, so the water stays visible.
    const radians = (angle * Math.PI) / 180;
    const x = (from[0] + to[0]) / 2 + Math.sin(radians) * 5;
    const y = (from[1] + to[1]) / 2 - Math.cos(radians) * 5;
    candidates.push({
      rank: river.rank,
      weight: bestLength,
      label: { x, y, angle, text: river.name },
      box: boxOf(x, y, width, radians),
    });
  }

  for (const lake of lakes) {
    const width = lake.name.length * CHAR_WIDTH;
    const xs = lake.ring.map(([x]) => x);
    const ys = lake.ring.map(([, y]) => y);
    const [left, right] = [Math.min(...xs), Math.max(...xs)];
    const [top, bottom] = [Math.min(...ys), Math.max(...ys)];
    if (right - left < width * 0.8 || bottom - top < LABEL_SIZE) continue;
    const x = (left + right) / 2;
    const y = (top + bottom) / 2 + LABEL_SIZE / 3;
    if (!inside([x, y])) continue;
    candidates.push({
      rank: lake.rank,
      weight: right - left,
      label: { x, y, angle: 0, text: lake.name },
      box: boxOf(x, y, width, 0),
    });
  }

  candidates.sort((a, b) => a.rank - b.rank || b.weight - a.weight);
  const taken: Obstacle[] = [...obstacles];
  const placed: WaterLabel[] = [];
  for (const candidate of candidates) {
    if (placed.length >= MAX_LABELS) break;
    const { box } = candidate;
    const onDrawing =
      box.left >= 2 &&
      box.right <= size.width - 2 &&
      box.top >= 2 &&
      box.bottom <= size.height - 2;
    if (!onDrawing || taken.some((other) => overlaps(box, other))) continue;
    // One name per river, however many of its pieces qualified.
    if (placed.some((label) => label.text === candidate.label.text)) continue;
    taken.push(box);
    placed.push(candidate.label);
  }
  return placed;
}

function lengthOf(points: readonly Point[]): number {
  let length = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (a !== undefined && b !== undefined)
      length += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return length;
}

/** The point `distance` along a polyline. */
function pointAt(points: readonly Point[], distance: number): Point {
  let left = distance;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (a === undefined || b === undefined) continue;
    const step = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (step >= left && step > 0) {
      const t = left / step;
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
    left -= step;
  }
  return points[points.length - 1] ?? [0, 0];
}

/** The upright box round a label of this width, turned by `radians` about
 *  its centre — generous, so two labels are judged to touch a little early. */
function boxOf(x: number, y: number, width: number, radians: number): Obstacle {
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const halfWidth = (width * cos + LABEL_SIZE * sin) / 2 + 2;
  const halfHeight = (width * sin + LABEL_SIZE * cos) / 2 + 2;
  return {
    left: x - halfWidth,
    right: x + halfWidth,
    top: y - halfHeight,
    bottom: y + halfHeight,
  };
}

function overlaps(a: Obstacle, b: Obstacle): boolean {
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}
